import { useCallback } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { CouchTopRow, couchTopRowsState } from '~/state/atoms/couchTopAtoms';

/**
 * The couch-top row placed against `seriesUid`, or `null` when none has
 * been placed on that series (or there is no series). The only read path
 * for `couchTopRowsState`: a caller names the series it is about to use
 * the row on, so it cannot be handed a row derived against another one.
 */
export function useCouchTopRow(
    seriesUid: string | undefined
): CouchTopRow | null {
    const rows = useRecoilValue(couchTopRowsState);
    return seriesUid === undefined ? null : (rows.get(seriesUid) ?? null);
}

/**
 * The only write path for `couchTopRowsState`. `seriesUid` must be the
 * series the placement was made AGAINST — the one the caller closed over,
 * not whatever is selected when it fires.
 */
export function useSetCouchTopRow(): (
    seriesUid: string,
    row: CouchTopRow
) => void {
    const setRows = useSetRecoilState(couchTopRowsState);
    return useCallback(
        (seriesUid, row) => {
            setRows((prev) => new Map(prev).set(seriesUid, row));
        },
        [setRows]
    );
}

/**
 * Discards every placement. Called when the couch panel closes, so a
 * placement the user decided against cannot be applied on a later visit,
 * nor carried onto a different structure set on the same CT — the panel
 * is the only thing that can create or apply one, so nothing outside it
 * has a use for a placement that outlives it.
 */
export function useCouchTopRowReset(): () => void {
    return useResetRecoilState(couchTopRowsState);
}
